import {
  recordTypes,
  isMappedRecordProperty,
  isNestedRecordProperty,
} from './record';
import { matchedItem, matchedUserAudit, matchedMeta } from './subObject';
import {
  broDnsAnswer,
  broFtpCmdarg,
  broFtpDataChannel,
  broSmbCommand,
  broSmbFile,
  broSmbMapping,
  broSmbTree,
  broSmtpHeaders,
} from './broRecord';
import { memoize } from 'lodash-es';

const objectTypes = {
  broDnsAnswer,
  broFtpCmdarg,
  broFtpDataChannel,
  broSmbCommand,
  broSmbFile,
  broSmbMapping,
  broSmbTree,
  broSmtpHeaders,
  matchedItem,
  matchedMeta,
  matchedUserAudit,
};

const primitiveTypes = [
  'boolean',
  'double',
  'float',
  'int',
  'long',
  'string',
  'timestamp',
  'unformattedInt',
  'uppercaseString',
];

const isArrayRecordProperty = (propName: string) =>
  recordTypes.hasOwnProperty(propName) &&
  [
    'arrayBroDnsAnswer',
    'arrayBroFtpCmdarg',
    'arrayMatchedItem',
    'arrayString',
  ].includes(recordTypes[propName]);

const getAllKeys = memoize(() => [
  ...Object.keys(broDnsAnswer),
  ...Object.keys(broFtpCmdarg),
  ...Object.keys(broFtpDataChannel),
  ...Object.keys(broSmbCommand),
  ...Object.keys(broSmbFile),
  ...Object.keys(broSmbMapping),
  ...Object.keys(broSmbTree),
  ...Object.keys(broSmtpHeaders),
  ...Object.keys(matchedItem),
  ...Object.keys(matchedMeta),
  ...Object.keys(matchedUserAudit),
  ...Object.keys(recordTypes),
]);

const arrayObjectTypes = {
  arrayBroDnsAnswer: broDnsAnswer,
  arrayBroFtpCmdarg: broFtpCmdarg,
  arrayMatchedItem: matchedItem,
};

const validateArrayToken = (parentToken: string, remainingTokens: string[]) => {
  const token = remainingTokens.length ? remainingTokens[0] : null;
  const expectedType =
    recordTypes.hasOwnProperty(parentToken) && recordTypes[parentToken];

  if (expectedType === 'arrayString') {
    // The token is already a string.
    return true;
  } else if (expectedType) {
    if (!arrayObjectTypes.hasOwnProperty(expectedType)) {
      return false;
    }

    // Array object types may not have child properties. There are no child
    // tokens that are arrays.
    const objectType = arrayObjectTypes[expectedType];
    const nextType: string | false =
      !!objectType.hasOwnProperty(token) && objectType[token];

    // There is an expected next type. If it does not match, the tokens are not
    // valid. Continue validation.
    return nextType && validateTokens(remainingTokens, nextType);
  } else {
    // There is no defined expected type. No further validation required.
    return true;
  }
};

/**
 * Validate schema tokens. Currently, only Schema V3 is supported.
 */
// complexity 17
// eslint-disable-next-line complexity
export const validateTokens = (
  originalTokens: string[],
  enforcedType?: string
) => {
  // Get the first token. The array will be shifted.
  const tokens = [...originalTokens];
  const firstToken = tokens.shift();

  // If the enforced type is primitive, the tokens are valid. Check this before
  // other tokens because some primitive values may not be in the schema.
  // There is not expected to be remaining tokens after primitive values.
  if (enforcedType && primitiveTypes.includes(enforcedType)) {
    return !tokens.length;
  }

  // Verify token array length. The firstToken will be undefined if the original
  // array was empty. Verify the passed token is acceptable.
  if (!firstToken || !getAllKeys().includes(firstToken)) {
    return false;
  }

  // Verify the next value in the mapped property is expected. The previous
  // token is expected to be a mapped object if `enforcedType` is defined.
  let nextEnforcedType: string | undefined;
  if (enforcedType) {
    // Remaining tokens are expected to match object definitions. If they do
    // not, the token is not valid.
    if (
      !objectTypes.hasOwnProperty(enforcedType) ||
      !objectTypes[enforcedType].hasOwnProperty(firstToken)
    ) {
      return false;
    }

    nextEnforcedType = objectTypes[enforcedType][firstToken];
  }

  if (isArrayRecordProperty(firstToken)) {
    // Verify the second item is an integer.
    const secondToken = tokens.shift();

    // The array must be indexed.
    const isValid = !!secondToken && !isNaN(parseInt(secondToken, 10));

    return isValid && validateArrayToken(firstToken, tokens);
  } else if (isMappedRecordProperty(firstToken)) {
    // Remaining tokens are expected for mapped record properties.
    const expectedType =
      recordTypes[firstToken] === 'mapDouble' ? 'double' : 'string';

    return !!tokens.length && validateTokens(tokens, expectedType);
  } else if (isNestedRecordProperty(firstToken)) {
    // For mapped record properties. Get the next expected token type.
    nextEnforcedType =
      (recordTypes.hasOwnProperty(firstToken) && recordTypes[firstToken]) ||
      undefined;

    // Validate remaining tokens.
    return validateTokens(tokens, nextEnforcedType);
  } else {
    // A primitive is valid if there are no remaining tokens.
    // verify there are no more tokens.
    return !tokens.length;
  }
};
